import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

// ── palette & tokens ──────────────────────────────────────────────────────────
const C = {
  bg: '#F7F7F5',
  surface: '#FFFFFF',
  border: '#E8E6E1',
  muted: '#9B9890',
  text: '#1A1917',
  accent: '#2563EB',
  accentLight: '#EEF3FD',
  green: '#16A34A',
  greenLight: '#F0FDF4',
  red: '#DC2626',
};

// ── Avatar with initials ──────────────────────────────────────────────────────
function InitialAvatar({ name, size = 40, backgroundColor = C.accent }) {
  const getInitials = (n) => {
    return n
      .split(' ')
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  };

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: size * 0.35,
        flexShrink: 0,
        border: `2px solid ${C.surface}`,
        boxShadow: `0 2px 4px rgba(0,0,0,0.1)`,
      }}
    >
      {getInitials(name)}
    </div>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────────
function Badge({ children, color = C.muted }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 99,
        background: C.bg,
        border: `1px solid ${C.border}`,
        fontSize: 11,
        fontWeight: 600,
        color,
        letterSpacing: '0.02em',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </span>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────
function fmtDate(s, opts = { day: 'numeric', month: 'short', year: 'numeric' }) {
  return s ? new Date(s).toLocaleDateString('en-US', opts) : '—';
}
function fmtDateTime(s) {
  return s ? new Date(s).toLocaleString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
}


function getAirportCode(flight) {
  const value = (flight?.arrival || '').trim();
  if (!value) return '—';

  const codeMatch = value.toUpperCase().match(/\b[A-Z]{3}\b/);
  const code = codeMatch ? codeMatch[0] : '';

  let name = value;

  if (code) {
    name = value.replace(code, '').replace(/^[-–—\s]+/, '').trim();
  }

  if (!code && !name) return '—';
  if (code && name) return `${code} (${name})`;
  if (code) return code;
  return name;
}

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(n => n[0].toUpperCase())
    .join('') || '??';
}

// ── Main component ────────────────────────────────────────────────────────────
export default function TravelSummary() {
  const { id } = useParams();
  const [race, setRace] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [flights, setFlights] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [passengersByRental, setPassengersByRental] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`/api/races/${id}`, { headers }).then(r => r.json()),
      fetch(`/api/races/${id}/attendees`, { headers }).then(r => r.json()),
      fetch(`/api/flights?race_id=${id}`, { headers }).then(r => r.json()),
      fetch(`/api/races/${id}/hotels`, { headers }).then(r => r.json()),
      fetch(`/api/car_rentals`, { headers }).then(r => r.json()),
    ]).then(async ([race, attendees, flights, hotels, rentals]) => {
      const filteredRentals = (rentals || []).filter(x => String(x.race_id) === String(id));
      
      // Fetch passengers for each rental
      const passengerMap = {};
      for (const rental of filteredRentals) {
        try {
          const passengerData = await fetch(`/api/car_rentals/${rental.id}/passengers`, { headers }).then(r => r.json());
          passengerMap[rental.id] = passengerData || [];
        } catch {
          passengerMap[rental.id] = [];
        }
      }
      
      setRace(race);
      setAttendees(attendees || []);
      setFlights(flights || []);
      setHotels(hotels || []);
      setRentals(filteredRentals);
      setPassengersByRental(passengerMap);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: 32, fontFamily: 'inherit', color: C.muted }}>Loading…</div>;
  if (!race || race.msg) return <div style={{ padding: 32, fontFamily: 'inherit', color: C.muted }}>Race not found</div>;

  // Get a random color for each attendee based on their ID
  const getColorForUser = (userId) => {
    const colors = [
      '#3B82F6', // blue
      '#8B5CF6', // purple
      '#EC4899', // pink
      '#F59E0B', // amber
      '#10B981', // emerald
      '#06B6D4', // cyan
      '#EF4444', // red
    ];
    return colors[userId % colors.length];
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', system-ui, sans-serif", color: C.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '14px 20px' }}>
          <Link
            to={`/races/${id}`}
            style={{ color: C.accent, textDecoration: 'none', fontSize: 12, fontWeight: 600 }}
          >
            ← Back to {race.name}
          </Link>
          <h1 style={{ margin: '8px 0 0 0', fontSize: 24, fontWeight: 700 }}>Travel Summary</h1>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.muted, marginTop: 6 }}>
            <span>📅 {fmtDate(race.start_date)} – {fmtDate(race.end_date)}</span>
            <span>{attendees.length} attendees</span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          {attendees.map(user => {
            const userFlights = flights.filter(f => f.user_id === user.id).sort((a, b) => new Date(a.departure_time) - new Date(b.departure_time));
            const userRentals = rentals.filter(r => r.user_id === user.id);
            const color = getColorForUser(user.id);

            // Only show if user has car rental
            if (userRentals.length === 0) return null;

            return (
                <div
                key={user.id}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 20,
                    flex: '0 1 auto',
                    minWidth: '400px',
                    width: 'fit-content',
                }}
                >
                {/* Main content - one row per rental */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {userRentals.map((rental, rentalIdx) => {
                    const primaryFlight = userFlights[0]; // First flight for arrival time
                    const storeInitials = rental.store?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'CR';
                    const arrivalTime = primaryFlight ? new Date(primaryFlight.arrival_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '—';
                    
                    // Get passengers for this specific rental from database
                    const rentalPassengerData = passengersByRental[rental.id] || [];
                    const passengers = rentalPassengerData
                      .map(p => attendees.find(a => a.id === p.user_id))
                      .filter(p => p); // Filter out any null values

                    return (
                        <div
                        key={rentalIdx}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}
                        >
                        <div
                            style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: C.text,
                            marginLeft: 4,
                            background: 'transparent',
                            boxShadow: 'none',
                            }}
                        >
                            {getAirportCode(primaryFlight)}
                        </div>

                        <div
                            style={{
                            background: C.surface,
                            border: `1px solid ${C.border}`,
                            borderRadius: 16,
                            padding: 24,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'box-shadow 0.2s, transform 0.2s',
                            }}
                            onMouseOver={e => {
                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            }}
                            onMouseOut={e => {
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'nowrap' }}>
                            {/* Driver initials */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                backgroundColor: color,
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: 18,
                                flexShrink: 0,
                            }}>
                                {getInitials(user.name)}
                            </div>

                            {/* Flight number */}
                            <div style={{
                                fontFamily: "'DM Mono', monospace",
                                fontWeight: 700,
                                fontSize: 16,
                                color: C.text,
                                minWidth: 80,
                                flexShrink: 0
                            }}>
                                {primaryFlight?.flight_number || '—'}
                            </div>

                            {/* Arrival time */}
                            <div style={{
                                fontSize: 16,
                                fontWeight: 600,
                                color: C.text,
                                minWidth: 70,
                                textAlign: 'center',
                                flexShrink: 0
                            }}>
                                {arrivalTime}
                            </div>

                            {/* Store initials */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                background: '#FEF3C7',
                                color: '#D97706',
                                fontWeight: 700,
                                fontSize: 14,
                                flexShrink: 0,
                            }}>
                                {storeInitials}
                            </div>

                            {/* Passengers */}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                                {passengers.length > 0 ? (
                                <>
                                    <span style={{
                                    fontSize: 12,
                                    color: C.muted,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    flexShrink: 0
                                    }}>
                                    Passengers:
                                    </span>

                                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    {passengers.map(passenger => (
                                        <div
                                        key={passenger.id}
                                        title={passenger.name}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            backgroundColor: getColorForUser(passenger.id),
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: 12,
                                            flexShrink: 0,
                                        }}
                                        >
                                        {getInitials(passenger.name)}
                                        </div>
                                    ))}
                                    </div>
                                </>
                                ) : (
                                <span style={{
                                    fontSize: 12,
                                    color: C.muted,
                                    fontStyle: 'italic',
                                    flexShrink: 0
                                }}>
                                    Solo
                                </span>
                                )}
                            </div>
                            </div>
                        </div>
                        </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {rentals.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: C.muted }}>
            No car rentals for this race
          </div>
        )}
      </div>
    </div>
  );
}
